import type {
	ActionLabelKey,
	MeetingActionConfluenceContentType,
	MeetingActionItemCta,
	MeetingActionType,
} from './meetingTypes.ts';

// `ActionLabelKey` is defined in `meetingTypes.ts` (a leaf type) — import it
// from there directly rather than re-exporting it here (re-exports trip the
// no-barrel-files rule).

/**
 * English fallback strings, kept co-located with the key definition so that:
 *  - the mapper can populate `MeetingActionItemCta.label` (used by tests and
 *    non-React consumers like analytics/mocks) without pulling in `react-intl`;
 *  - the UI can source display copy from `<FormattedMessage>` (see ActionItem).
 *
 * If either changes, keep both in sync.
 */
export const ACTION_LABEL_FALLBACK: Record<ActionLabelKey, string> = {
	CREATE_PAGE: 'Create page',
	CREATE_WHITEBOARD: 'Create whiteboard',
	CREATE_DATABASE: 'Create database',
	CREATE_SLIDES: 'Create slides',
	AUTO_UPDATE_JIRA: 'Auto-update Jira',
	GENERATE_MESSAGE: 'Generate message',
};

type ActionLike = {
	type: MeetingActionType;
	confluenceContentType?: MeetingActionConfluenceContentType;
};

/**
 * Derive the stable CTA label key for a 1-click action from its typed enums.
 *
 * Fallback rules:
 *  - CONFLUENCE with an unknown / missing `confluenceContentType` → 'CREATE_PAGE'
 *    (safe generic that matches the LIVE_DOC copy).
 */
export const getActionLabelKey = (action: ActionLike): ActionLabelKey => {
	switch (action.type) {
		case 'JIRA':
			return 'AUTO_UPDATE_JIRA';
		case 'MESSAGE':
			return 'GENERATE_MESSAGE';
		case 'CONFLUENCE':
		default:
			switch (action.confluenceContentType) {
				case 'WHITEBOARD':
					return 'CREATE_WHITEBOARD';
				case 'DATABASE':
					return 'CREATE_DATABASE';
				case 'SLIDES':
					return 'CREATE_SLIDES';
				case 'LIVE_DOC':
				default:
					return 'CREATE_PAGE';
			}
	}
};

/** Convenience: derive the English fallback label directly from an action. */
export const getActionLabelFallback = (action: MeetingActionItemCta): string =>
	ACTION_LABEL_FALLBACK[getActionLabelKey(action)];
